import React, { memo } from "react";
import { BaseEdge, Edge, EdgeLabelRenderer, EdgeProps, getBezierPath } from "@xyflow/react";

export type WorkflowEdgeData = {
    kind: 'flow' | 'route' | 'error' | 'slot';
    filterOf: number | null;
    filterName?: string;
    hasFilter: boolean;
    routeIndex?: number;
    onFilter: (moduleId: number) => void;
    onRemoveRoute?: (routerId: number, routeIndex: number) => void;
    routerId?: number;
};

export type WorkflowFlowEdge = Edge<WorkflowEdgeData, 'workflow'>;

const STYLE: Record<WorkflowEdgeData['kind'], React.CSSProperties> = {
    flow: { stroke: '#adb5bd', strokeWidth: 2 },
    route: { stroke: '#adb5bd', strokeWidth: 2 },
    error: { stroke: '#f06548', strokeWidth: 2, strokeDasharray: '6 4' },
    slot: { stroke: '#ced4da', strokeWidth: 1.5, strokeDasharray: '3 4' },
};

/**
 * A link between modules. Flow and route links carry the filter of the module
 * they lead into (Make keeps filters there), edited through the wrench.
 */
const WorkflowEdge = (props: EdgeProps<WorkflowFlowEdge>) => {
    const [path, labelX, labelY] = getBezierPath(props);
    const data = props.data;
    const canFilter = !!data && (data.kind === 'flow' || data.kind === 'route') && data.filterOf !== null;

    return (
        <>
            <BaseEdge id={props.id} path={path} style={STYLE[data?.kind || 'flow']} />
            {(canFilter || (data?.kind === 'slot' && data.routeIndex !== undefined)) &&
                <EdgeLabelRenderer>
                    <div
                        className="nodrag nopan position-absolute d-flex align-items-center gap-2"
                        style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`, pointerEvents: 'all' }}
                    >
                        {canFilter &&
                            <button
                                type="button"
                                className={"btn rounded-circle p-0 d-inline-flex align-items-center justify-content-center shadow-sm " + (data!.hasFilter ? 'btn-warning' : 'btn-light border')}
                                style={{ width: 31, height: 31 }}
                                title={data!.filterName || ''}
                                onClick={() => data!.onFilter(data!.filterOf as number)}
                            >
                                <i className={(data!.hasFilter ? "ri-filter-3-fill" : "ri-tools-line") + " fs-15"} />
                            </button>
                        }
                        {data?.routeIndex !== undefined && data.onRemoveRoute && data.routerId !== undefined && (data.kind === 'slot' || data.kind === 'route') &&
                            <button
                                type="button"
                                className="btn btn-light border rounded-circle p-0 text-danger d-inline-flex align-items-center justify-content-center shadow-sm"
                                style={{ width: 26, height: 26 }}
                                onClick={() => data.onRemoveRoute!(data.routerId as number, data.routeIndex as number)}
                            >
                                <i className="ri-close-line fs-14" />
                            </button>
                        }
                    </div>
                </EdgeLabelRenderer>
            }
        </>
    );
};

export default memo(WorkflowEdge);
